/**
 * Friends Page
 * Freundesliste, Anfragen und Freundes-Profile
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Friends.module.css';
import { friendsApi, userApi, getMediaUrl } from '../api';

function Friends() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Add Friend Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  
  // Daten laden
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [friendsResult, requestsResult, outgoingResult] = await Promise.all([
        friendsApi.getFriends(),
        friendsApi.getIncomingRequests(),
        friendsApi.getOutgoingRequests()
      ]);

      if (friendsResult?.success) {
        // API v1.2: data ist direkt das Array
        setFriends(Array.isArray(friendsResult.data) ? friendsResult.data : []);
      } else {
        setFriends([]);
      }

      if (requestsResult?.success) {
        setRequests(Array.isArray(requestsResult.data) ? requestsResult.data : []);
      } else {
        setRequests([]);
      }

      if (outgoingResult?.success) {
        setOutgoingRequests(Array.isArray(outgoingResult.data) ? outgoingResult.data : []);
      } else {
        setOutgoingRequests([]);
      }
    } catch (err) {
      setError(t('friends.loadError'));
      setFriends([]);
      setRequests([]);
      console.error('Friends load error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Benutzername suchen
  const handleSearchUser = async (e) => {
    e.preventDefault();
    const query = searchUsername.trim();
    if (!query) return;

    setIsSearching(true);
    setSearchResults([]);
    setMessage(null);

    try {
      const result = await userApi.searchUsers(query);
      if (result.success) {
        const items = result.data?.items || [];
        if (items.length === 0) {
          setMessage({ type: 'error', text: t('friends.userNotFound', { query }) });
        } else {
          setSearchResults(items);
        }
      } else {
        // Fallback: direkt als user_id versuchen (falls Zahl eingegeben)
        const asId = parseInt(query, 10);
        if (!isNaN(asId)) {
          await handleSendRequestById(asId, `#${asId}`);
        } else {
          setMessage({ type: 'error', text: result.error?.message || t('friends.searchFailed') });
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('friends.searchNetworkError') });
    } finally {
      setIsSearching(false);
    }
  };

  const handleSendRequestById = async (userId, displayName) => {
    setIsSending(true);
    try {
      const result = await friendsApi.sendRequest(userId);
      if (result.success) {
        setMessage({ type: 'success', text: t('friends.requestSent', { name: displayName }) });
        setSearchUsername('');
        setSearchResults([]);
        setShowAddModal(false);
        loadData();
      } else {
        const code = result.error?.code;
        if (code === 'ALREADY_FRIENDS') {
          setMessage({ type: 'error', text: t('friends.alreadyFriends') });
        } else if (code === 'REQUEST_EXISTS') {
          setMessage({ type: 'error', text: t('friends.requestExists', { name: displayName }) });
        } else {
          setMessage({ type: 'error', text: result.error?.message || t('friends.requestFailed') });
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('friends.networkError') });
    } finally {
      setIsSending(false);
    }
  };

  // Anfrage akzeptieren
  const handleAcceptRequest = async (requestId, username) => {
    try {
      const result = await friendsApi.acceptRequest(requestId);

      if (result.success) {
        setMessage({ type: 'success', text: t('friends.nowFriends', { username }) });
        loadData(); // Neu laden
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('friends.acceptError') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('friends.networkError') });
    }
  };

  // Anfrage ablehnen
  const handleRejectRequest = async (requestId) => {
    try {
      const result = await friendsApi.rejectRequest(requestId);

      if (result.success) {
        setRequests(prev => prev.filter(r => r.request_id !== requestId));
        setMessage({ type: 'success', text: t('friends.requestRejected') });
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('friends.rejectError') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('friends.networkError') });
    }
  };

  // Öffnet die echte Profilseite des Freundes (/profile/:username), wo auch
  // "Freund entfernen" verfügbar ist
  const openFriendDetail = (friend) => {
    navigate(`/profile/${encodeURIComponent(friend.username)}`);
  };

  // Anfragen-Zähler - sicherstellen dass requests ein Array ist
  const safeRequests = Array.isArray(requests) ? requests : [];
  const safeOutgoingRequests = Array.isArray(outgoingRequests) ? outgoingRequests : [];
  const pendingRequestsCount = safeRequests.length;

  if (isLoading) {
    return (
      <div className={styles.friendsContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>{t('friends.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.friendsContainer}>
      {/* Header */}
      <div className={styles.friendsHeader}>
        <div className={styles.headerContent}>
          <h1>{t('friends.title')}</h1>
          <p>{t('friends.subtitle')}</p>
        </div>
        <button className={styles.addFriendBtn} onClick={() => setShowAddModal(true)}>
          <span>➕</span>
          {t('friends.addFriend')}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          <span>{message.type === 'success' ? '✅' : '❌'}</span>
          <p>{message.text}</p>
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'friends' ? styles.active : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          👥 {t('friends.tabFriends', { count: friends.length })}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'requests' ? styles.active : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          📬 {t('friends.tabRequests')}
          {pendingRequestsCount > 0 && (
            <span className={styles.badge}>{pendingRequestsCount}</span>
          )}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'friends' && (
        <div className={styles.friendsGrid}>
          {friends.length === 0 ? (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>👥</span>
              <h3>{t('friends.noFriendsYet')}</h3>
              <p>{t('friends.noFriendsText')}</p>
              <button className={styles.primaryBtn} onClick={() => setShowAddModal(true)}>
                {t('friends.addFriend')}
              </button>
            </div>
          ) : (
            friends.map(friend => (
              <div
                key={friend.user_id}
                className={styles.friendCard}
                onClick={() => openFriendDetail(friend)}
              >
                <div className={styles.friendAvatar}>
                  {friend.avatar_media_key ? (
                    <img src={getMediaUrl(friend.avatar_media_key)} alt={friend.username} />
                  ) : (
                    <span>{friend.username?.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className={styles.friendInfo}>
                  <h3>{friend.username}</h3>
                </div>
                <button
                  className={styles.viewBtn}
                  onClick={(e) => { e.stopPropagation(); openFriendDetail(friend); }}
                >
                  {t('friends.view')}
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'requests' && (
        <div className={styles.requestsList}>
          {safeRequests.length === 0 && safeOutgoingRequests.length === 0 ? (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>📭</span>
              <h3>{t('friends.noRequests')}</h3>
              <p>{t('friends.noRequestsText')}</p>
            </div>
          ) : (
            <>
              {/* Eingehende Anfragen */}
              {safeRequests.length > 0 && (
                <div className={styles.requestSection}>
                  <h3>{t('friends.incomingRequests')}</h3>
                  {safeRequests.map(request => (
                    <div key={request.request_id} className={styles.requestCard}>
                      <div className={styles.requestAvatar}>
                        {request.avatar_media_key ? (
                          <img src={getMediaUrl(request.avatar_media_key)} alt={request.username} />
                        ) : (
                          <span>{request.username?.charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className={styles.requestInfo}>
                        <h4>{request.username}</h4>
                        <p>{t('friends.wantsToBeFriend')}</p>
                      </div>
                      <div className={styles.requestActions}>
                        <button
                          className={styles.acceptBtn}
                          onClick={() => handleAcceptRequest(request.request_id, request.username)}
                        >
                          ✓ {t('friends.accept')}
                        </button>
                        <button
                          className={styles.rejectBtn}
                          onClick={() => handleRejectRequest(request.request_id)}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Ausgehende Anfragen */}
              {safeOutgoingRequests.length > 0 && (
                <div className={styles.requestSection}>
                  <h3>{t('friends.outgoingRequests')}</h3>
                  {safeOutgoingRequests.map(request => (
                    <div key={request.request_id} className={`${styles.requestCard} ${styles.outgoing}`}>
                      <div className={styles.requestAvatar}>
                        {request.avatar_media_key ? (
                          <img src={getMediaUrl(request.avatar_media_key)} alt={request.username} />
                        ) : (
                          <span>{request.username?.charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className={styles.requestInfo}>
                        <h4>{request.username}</h4>
                        <p>{t('friends.requestPending')}</p>
                      </div>
                      <span className={styles.pendingBadge}>{t('friends.pending')}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add Friend Modal */}
      {showAddModal && (
        <div className={styles.modalOverlay} onClick={() => { setShowAddModal(false); setSearchResults([]); setSearchUsername(''); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{t('friends.addFriend')}</h2>
              <button className={styles.closeBtn} onClick={() => { setShowAddModal(false); setSearchResults([]); setSearchUsername(''); }}>×</button>
            </div>
            <form onSubmit={handleSearchUser}>
              <div className={styles.formGroup}>
                <label>{t('friends.username')}</label>
                <input
                  type="text"
                  value={searchUsername}
                  onChange={(e) => { setSearchUsername(e.target.value); setSearchResults([]); }}
                  placeholder={t('friends.usernamePlaceholder')}
                  autoFocus
                />
              </div>
              <div className={styles.modalActions}>
                <button type="button" className={styles.cancelBtn} onClick={() => { setShowAddModal(false); setSearchResults([]); setSearchUsername(''); }}>
                  {t('friends.cancel')}
                </button>
                <button type="submit" className={styles.submitBtn} disabled={isSearching || isSending || !searchUsername.trim()}>
                  {isSearching ? t('friends.searchingShort') : t('friends.search')}
                </button>
              </div>
            </form>

            {/* Suchergebnisse */}
            {searchResults.length > 0 && (
              <div className={styles.searchResultsList}>
                {searchResults.map(user => (
                  <div key={user.id} className={styles.searchResultItem}>
                    <div className={styles.searchResultAvatar}>
                      {user.avatar_media_key
                        ? <img src={getMediaUrl(user.avatar_media_key)} alt={user.username} />
                        : <span>{user.username.charAt(0).toUpperCase()}</span>}
                    </div>
                    <span className={styles.searchResultName}>{user.username}</span>
                    <button
                      className={styles.submitBtn}
                      disabled={isSending}
                      onClick={() => handleSendRequestById(user.id, user.username)}
                    >
                      {isSending ? '...' : t('friends.sendRequest')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default Friends;
